import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  IconButton,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SendIcon from '@mui/icons-material/Send';
import { colors } from '../../theme/theme';
import { useAuthService } from '../../hooks/useAuthService';
import { landingPath } from '../../auth/session';
import { COUNTRIES, DEFAULT_COUNTRY, Country } from '../../constants/countries';
import { validatePhone } from '../../utils/utils';

const SLIDES = [
  {
    image: 'https://images.unsplash.com/photo-1556741533-6e6a62bd8b49?w=1200&q=80',
    tag: 'Queue Management System',
    title: 'Manage Queues Effortlessly.',
    highlight: 'Smart tokens. Happy customers',
    description:
      'Digital queue management, real-time token tracking, and seamless customer experience — all in one powerful platform.',
  },
  {
    image: 'https://images.unsplash.com/photo-1577962917302-cd874c4e31d2?w=1200&q=80',
    tag: 'Queue Management System',
    title: 'Reduce Wait Times.',
    highlight: 'Boost satisfaction',
    description:
      'Let your customers book tokens in advance, track their position in queue, and arrive just in time for their turn.',
  },
  {
    image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1200&q=80',
    tag: 'Queue Management System',
    title: 'Scale Your Business.',
    highlight: 'Multiple queues & providers',
    description:
      'Create multiple queues, assign providers, manage schedules, and handle walk-in customers with QR code scanning.',
  },
];

const LoginScreen: React.FC = () => {
  const navigate = useNavigate();
  const { sendOTP, isAuthenticated } = useAuthService();

  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [selectedCountry, setSelectedCountry] = useState<Country>(DEFAULT_COUNTRY);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showSnackbar, setShowSnackbar] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [currentSlide, setCurrentSlide] = useState(0);

  // Auto-rotate slides
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % SLIDES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  // Redirect if already authenticated. { replace: true } so that pressing Back
  // onto /login after logging in immediately bounces forward to /dashboard
  // instead of leaving the user stranded on the login screen.
  useEffect(() => {
    if (isAuthenticated()) {
      navigate(landingPath(), { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const isPhoneValid = useCallback((): boolean => {
    if (!phoneNumber.trim()) return false;
    const fullPhoneNumber = `${selectedCountry.dialCode}${phoneNumber}`;
    return validatePhone(fullPhoneNumber, selectedCountry.locale);
  }, [phoneNumber, selectedCountry]);

  const handlePhoneChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/[^0-9]/g, '');
    setPhoneNumber(value);
  }, []);

  const handleCountryChange = useCallback((event: any) => {
    const country = COUNTRIES.find((c) => c.dialCode === event.target.value);
    if (country) setSelectedCountry(country);
  }, []);

  const handleSendCode = useCallback(async () => {
    if (!isPhoneValid()) return;
    setIsLoading(true);
    try {
      await sendOTP(phoneNumber, selectedCountry.dialCode);
      navigate('/otp', {
        state: { phoneNumber, countryCode: selectedCountry.dialCode },
      });
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 'Failed to send OTP. Please try again.';
      setSnackbarMessage(errorMessage);
      setShowSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  }, [phoneNumber, selectedCountry, isPhoneValid, sendOTP, navigate]);

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && isPhoneValid() && !isLoading) {
        handleSendCode();
      }
    },
    [isPhoneValid, isLoading, handleSendCode]
  );

  const phoneValid = isPhoneValid();
  const slide = SLIDES[currentSlide];

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Left Side — Image Carousel */}
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          display: { xs: 'none', md: 'block' },
        }}
      >
        {/* Background Image */}
        {SLIDES.map((s, index) => (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url(${s.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: index === currentSlide ? 1 : 0,
              transition: 'opacity 0.8s ease-in-out',
            }}
          />
        ))}

        {/* Dark Overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.7) 100%)',
          }}
        />

        {/* Tag Pill — Top Left */}
        <Box
          sx={{
            position: 'absolute',
            top: 32,
            left: 32,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            backgroundColor: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(10px)',
            borderRadius: '24px',
            px: 2.5,
            py: 1,
          }}
        >
          <Box component="img" src="/logo192.png" alt="Logo" sx={{ width: 32, height: 32, borderRadius: '50%' }} />
          <Typography sx={{ color: '#fff', fontWeight: 500, fontSize: 14 }}>
            {slide.tag}
          </Typography>
        </Box>

        {/* Bottom Content */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            p: 4,
            pb: 5,
          }}
        >
          <Typography
            sx={{
              color: '#fff',
              fontWeight: 700,
              fontSize: 32,
              lineHeight: '40px',
              mb: 0.5,
            }}
          >
            {slide.title}
          </Typography>
          <Typography
            sx={{
              color: colors.primary,
              fontWeight: 700,
              fontSize: 32,
              lineHeight: '40px',
              mb: 1.5,
              background: `linear-gradient(90deg, #60A5FA, #34D399)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {slide.highlight}
          </Typography>
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.8)',
              fontSize: 15,
              lineHeight: '24px',
              maxWidth: 500,
              mb: 3,
            }}
          >
            {slide.description}
          </Typography>

          {/* Slide Indicators */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {SLIDES.map((_, index) => (
              <IconButton
                key={index}
                onClick={() => setCurrentSlide(index)}
                sx={{ p: 0 }}
              >
                <Box
                  sx={{
                    width: index === currentSlide ? 32 : 20,
                    height: 6,
                    borderRadius: '3px',
                    backgroundColor:
                      index === currentSlide ? '#34D399' : 'rgba(255,255,255,0.4)',
                    transition: 'all 0.3s ease',
                  }}
                />
              </IconButton>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Right Side — Login Form */}
      <Box
        sx={{
          width: { xs: '100%', md: '40%' },
          minWidth: { md: 400 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3, md: 5 },
          py: 4,
          backgroundColor: colors.backgroundPaper,
        }}
      >
        {/* Logo */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box component="img" src="/logo192.png" alt="Logo" sx={{ width: 60, height: 60, borderRadius: '50%', mx: 'auto', mb: 1 }} />
          <Typography sx={{ color: colors.textPrimary, fontWeight: 700, fontSize: 14, letterSpacing: 1 }}>
            BASEQUEUE PROVIDER
          </Typography>
        </Box>

        {/* Welcome Text */}
        <Typography
          sx={{
            color: colors.textPrimary,
            fontWeight: 700,
            fontSize: 28,
            lineHeight: '36px',
            mb: 0.5,
            textAlign: 'center',
          }}
        >
          Welcome back
        </Typography>
        <Typography
          sx={{
            color: colors.textSecondary,
            fontSize: 15,
            mb: 4,
            textAlign: 'center',
          }}
        >
          Sign in with your mobile number to continue
        </Typography>

        {/* Mobile Number Label */}
        <Box sx={{ width: '100%', maxWidth: 380 }}>
          <Typography
            sx={{
              color: colors.gray700,
              fontWeight: 500,
              fontSize: 14,
              mb: 1,
            }}
          >
            Mobile Number
          </Typography>

          {/* Phone Input */}
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            <FormControl sx={{ width: 110, flexShrink: 0 }}>
              <Select
                value={selectedCountry.dialCode}
                onChange={handleCountryChange}
                disabled={isLoading}
                IconComponent={KeyboardArrowDownIcon}
                sx={{
                  height: 52,
                  backgroundColor: colors.backgroundDefault,
                  borderRadius: '12px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    paddingRight: '28px !important',
                  },
                }}
                renderValue={() => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span style={{ fontSize: 18 }}>{selectedCountry.flag}</span>
                    <span style={{ color: colors.gray700, fontSize: 14 }}>
                      {selectedCountry.dialCode}
                    </span>
                  </Box>
                )}
                MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
              >
                {COUNTRIES.map((country) => (
                  <MenuItem key={country.id} value={country.dialCode}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <span style={{ fontSize: 18 }}>{country.flag}</span>
                      <span>{country.name}</span>
                      <span style={{ color: colors.textSecondary, marginLeft: 'auto' }}>
                        {country.dialCode}
                      </span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              value={phoneNumber}
              onChange={handlePhoneChange}
              onKeyPress={handleKeyPress}
              placeholder="Enter mobile number"
              disabled={isLoading}
              inputProps={{ maxLength: 12, style: { fontSize: 15 } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: 52,
                  borderRadius: '12px',
                  backgroundColor: colors.backgroundDefault,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '14px 16px',
                  '&::placeholder': { color: '#94A3B8', opacity: 1 },
                },
              }}
            />
          </Box>

          {/* Get OTP Button */}
          <Button
            fullWidth
            variant="contained"
            onClick={handleSendCode}
            disabled={isLoading || !phoneValid}
            endIcon={
              isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SendIcon sx={{ fontSize: 18 }} />
              )
            }
            sx={{
              height: 52,
              borderRadius: '12px',
              background: phoneValid
                ? `linear-gradient(90deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`
                : colors.gray200,
              color: phoneValid ? '#fff' : colors.textTertiary,
              fontWeight: 600,
              fontSize: 16,
              textTransform: 'none',
              boxShadow: phoneValid ? '0 4px 14px rgba(7, 104, 223, 0.3)' : 'none',
              '&:hover': {
                background: phoneValid
                  ? `linear-gradient(90deg, ${colors.primaryDark} 0%, ${colors.primary} 100%)`
                  : colors.gray200,
              },
              '&.Mui-disabled': {
                backgroundColor: colors.gray200,
                color: colors.textTertiary,
              },
            }}
          >
            Get OTP
          </Button>
        </Box>

        {/* Divider */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            maxWidth: 380,
            my: 3,
          }}
        >
          <Box sx={{ flex: 1, height: '1px', backgroundColor: '#E2E8F0' }} />
          <Typography sx={{ px: 2, color: colors.textTertiary, fontSize: 12, fontWeight: 500, letterSpacing: 1 }}>
            GET THE APP
          </Typography>
          <Box sx={{ flex: 1, height: '1px', backgroundColor: '#E2E8F0' }} />
        </Box>

        {/* App Store Buttons */}
        <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
          <Box
            component="a"
            href="https://play.google.com"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              border: '1px solid #E2E8F0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': { borderColor: colors.primary, backgroundColor: colors.primaryLight },
            }}
          >
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/d/d0/Google_Play_Arrow_logo.svg"
              alt="Google Play"
              style={{ width: 22, height: 22 }}
            />
          </Box>
          <Box
            component="a"
            href="https://apps.apple.com"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              border: '1px solid #E2E8F0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': { borderColor: colors.primary, backgroundColor: colors.primaryLight },
            }}
          >
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg"
              alt="App Store"
              style={{ width: 20, height: 24 }}
            />
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ color: colors.textTertiary, fontSize: 12 }}>
            Powered by{' '}
            <Box
              component="span"
              sx={{ color: colors.primary, fontWeight: 600 }}
            >
              BaseQueue Provider
            </Box>
          </Typography>
        </Box>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSnackbar(false)} severity="error" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LoginScreen;
